import { useBenefitDraftState, useBenefitSaving, useBenefitReviewActive } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitEligibilityOptions } from "@/features/benefit-workspace/useBenefitProgramReads"
import { IconHandStop } from "@tabler/icons-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { saveBenefitChange } from "@/lib/benefit-save"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { EditableCardActions } from "./field"
import type { BenefitDetailDto } from "@/lib/benefit-types"

/** One version-fenced request selector; grant/eligibility views share its saved readback only. */
export function RequestedBenefitsSection({ benefit, canEdit, onUpdated, readOnly = false }: {
  benefit: BenefitDetailDto; canEdit: boolean; onUpdated: (updated: BenefitDetailDto) => void; readOnly?: boolean
}) {
  const { options, loading, error } = useBenefitEligibilityOptions(benefit)
  const [selected, setSelected] = useBenefitDraftState("requested-benefits", "selected", [])
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "requested-benefits")
  const [pending, setSaving] = useBenefitSaving()
  const saving = useBenefitReviewActive() || pending
  const [capturedOptions, setCapturedOptions] = useBenefitDraftState("requested-benefits", "options", [])

  const savedSelection = () => [...new Set([
    ...benefit.requestedBenefits.map(item => item.programId),
    ...options.filter(option => option.requiredByExistingGrant).map(option => option.programId),
  ])]

  const selection = editing ? selected : savedSelection()
  const canOpen = canEdit && !readOnly && !loading && !error && !benefit.requestedBenefitsReadUnavailable
  const choicesCurrent = selected.every(id => options.filter(option => option.programId === id).length === 1)
    && options.every(option => !option.requiredByExistingGrant || selected.includes(option.programId))
  const canSave = canOpen && choicesCurrent
  const displayedOptions = editing ? capturedOptions : options

  const save = async () => {
    if (!editing || saving || !canSave) { toast.error("Reload requested-benefit choices before saving."); return }
    if (!token) { toast.error("Reload before changing requested benefits."); return }
    setSaving(true)
    const result = await saveBenefitChange(benefit.id, onUpdated,
      () => api.put<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/requested-benefits`, {
        concurrencyToken: token,
        programIds: [...new Set(selected)].sort((a, b) => a - b),
      }), "Requested benefits saved. No award or payment was created.")
    setSaving(false)
    if (result.saved || result.reloaded) setEditing(false)
  }

  return <Card>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><IconHandStop className="size-5" />Requested assistance</CardTitle>
          <CardDescription>{readOnly ? "Saved intent; edit requested benefits in Program Details." : "Choose the benefits this household is pursuing. This is intent, not an award or eligibility decision."}</CardDescription>
        </div>
        {!readOnly && <EditableCardActions isEditing={editing} isSaving={saving}
          canEdit={canOpen} saveDisabled={!canSave} restorePanelFocus
          onEdit={() => { if (!canOpen) return; setCapturedOptions(structuredClone(options)); setSelected(savedSelection()); setEditing(true) }}
          onCancel={() => setEditing(false)} onSave={save} />}
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      {benefit.requestedBenefitsReadUnavailable && <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Requested assistance could not be read from the application history. Reload before changing intent; an unavailable list is not the same as no request.</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading the active benefit catalog…</p>}
      {editing && !choicesCurrent && <p role="status">Requested-benefit choices or grant requirements changed. Original choices are retained; cancel and reopen to use the current catalog.</p>}
      {(editing || (!loading && !error)) && <div className="grid gap-3 sm:grid-cols-2">
        {selection.filter(id => !displayedOptions.some(option => option.programId === id)).map(id => <p key={id}>Program {id} — selected; unavailable in the captured catalog.</p>)}
        {displayedOptions.map((option, index) => {
          const pinned = option.requiredByExistingGrant === true
          const checked = selection.includes(option.programId) || (!editing && pinned)
          return <label key={`${option.programId}/${option.programYear}/${index}`} className="flex items-start gap-2 rounded-md border p-3 text-sm">
            <Checkbox aria-label={`Request ${option.name}`} checked={checked}
              disabled={!editing || readOnly || saving || pinned || !canOpen || options.filter(current => current.programId === option.programId).length !== 1}
              onCheckedChange={value => setSelected(current => value === true
                ? [...new Set([...current, option.programId])]
                : current.filter(id => id !== option.programId))} />
            <span><span className="font-medium">{option.name} {option.programYear}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{pinned ? "Existing current-year grant requirements are pinned." : "Request only; no financial row is created."}</span>
              {!option.benefitProgramInstance && <span className="block text-xs text-amber-700">Calculation settings are not yet available.</span>}
            </span>
          </label>
        })}
        {displayedOptions.length === 0 && <p className="text-sm text-muted-foreground">No active benefits are configured for this saved application date.</p>}
      </div>}
    </CardContent>
  </Card>
}
